import axios, { type AxiosInstance, AxiosError } from "axios";

/**
 * Client HTTP dédié aux endpoints `/auth/v2/*`. Volontairement séparé du `api`
 * principal pour deux raisons :
 *  - Aucun interceptor : les flux d'auth se passent du Bearer (login, refresh,
 *    reset, verify email) ou ajoutent leur propre Authorization (logout,
 *    change password, 2FA).
 *  - Pas de risque d'avoir un 401 qui rejoue à l'infini sur /refresh.
 */

const rawUrl = import.meta.env.VITE_API_URL;
const baseURL = rawUrl ? `${rawUrl.replace(/\/+$/, "")}/api` : "/api";

const buildClient = (): AxiosInstance =>
  axios.create({ baseURL, headers: { "Content-Type": "application/json" } });

const client = buildClient();

export interface TokensResponse {
  kind: "tokens";
  accessToken: string;
  refreshToken: string;
  accessExpiresIn: number;
  refreshExpiresIn: number;
  identity: {
    id: string;
    email: string;
    emailVerified: boolean;
    locale: string;
  };
}

export interface MfaRequiredResponse {
  kind: "totp_required";
  mfaChallenge: string;
}

export type LoginResponse = TokensResponse | MfaRequiredResponse;

export interface SignupResponse {
  ok: true;
  authIdentityId: string;
  message: string;
}

const unwrap = async <T>(p: Promise<{ data: T }>): Promise<T> => (await p).data;

export const authApi = {
  async signup(input: {
    email: string;
    password: string;
    audience: "pro" | "client";
    locale?: string;
    acceptedTerms: true;
  }): Promise<SignupResponse> {
    return unwrap(client.post<SignupResponse>("/auth/v2/signup", input));
  },

  async login(input: {
    email: string;
    password: string;
    totpCode?: string;
  }): Promise<LoginResponse> {
    return unwrap(client.post<LoginResponse>("/auth/v2/login", input));
  },

  async loginGoogle(input: {
    idToken: string;
    audience: "pro" | "client";
    acceptedTerms?: true;
  }): Promise<LoginResponse> {
    return unwrap(client.post<LoginResponse>("/auth/v2/login/google", input));
  },

  async completeMfa(input: {
    mfaChallenge: string;
    code: string;
  }): Promise<TokensResponse> {
    return unwrap(client.post<TokensResponse>("/auth/v2/login/mfa", input));
  },

  async refresh(refreshToken: string): Promise<{
    accessToken: string;
    refreshToken: string;
    accessExpiresIn: number;
    refreshExpiresIn: number;
  }> {
    return unwrap(client.post("/auth/v2/refresh", { refreshToken }));
  },

  async logout(opts: {
    accessToken: string;
    refreshToken?: string;
    all?: boolean;
  }): Promise<void> {
    await client.post(
      "/auth/v2/logout",
      { refreshToken: opts.refreshToken, all: opts.all },
      { headers: { Authorization: `Bearer ${opts.accessToken}` } },
    );
  },

  async requestPasswordReset(input: {
    email: string;
    audience: "pro" | "client";
  }): Promise<void> {
    await client.post("/auth/v2/password/reset/request", input);
  },

  async confirmPasswordReset(input: {
    token: string;
    password: string;
  }): Promise<void> {
    await client.post("/auth/v2/password/reset/confirm", input);
  },

  async changePassword(input: {
    accessToken: string;
    currentPassword: string;
    newPassword: string;
  }): Promise<void> {
    await client.post(
      "/auth/v2/password/change",
      {
        currentPassword: input.currentPassword,
        newPassword: input.newPassword,
      },
      { headers: { Authorization: `Bearer ${input.accessToken}` } },
    );
  },

  async verifyEmail(token: string): Promise<void> {
    await client.post("/auth/v2/email/verify", { token });
  },

  async startTotp(accessToken: string): Promise<{
    secret: string;
    otpauthUrl: string;
    qrCodeDataUrl: string;
  }> {
    return unwrap(
      client.post(
        "/auth/v2/totp/start",
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } },
      ),
    );
  },

  async confirmTotp(input: {
    accessToken: string;
    code: string;
  }): Promise<{ recoveryCodes: string[] }> {
    return unwrap(
      client.post(
        "/auth/v2/totp/confirm",
        { code: input.code },
        { headers: { Authorization: `Bearer ${input.accessToken}` } },
      ),
    );
  },

  async disableTotp(accessToken: string): Promise<void> {
    await client.post(
      "/auth/v2/totp/disable",
      {},
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
  },

  async regenerateRecoveryCodes(accessToken: string): Promise<{
    recoveryCodes: string[];
  }> {
    return unwrap(
      client.post(
        "/auth/v2/totp/recovery-codes/regenerate",
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } },
      ),
    );
  },
};

/**
 * Extrait un message d'erreur lisible depuis une réponse axios. Le serveur
 * renvoie souvent `{ message, reasons?, hint? }` (BadRequest / Unauthorized).
 */
export const extractAuthError = (err: unknown): string => {
  if (err instanceof AxiosError) {
    const data = err.response?.data as
      | { message?: string; hint?: string; reasons?: string[] }
      | undefined;
    if (data?.hint) return data.hint;
    if (typeof data?.message === "string") {
      if (data.message === "password_policy_violation") {
        return "Mot de passe trop faible : 12 caractères, majuscule, minuscule, chiffre, symbole.";
      }
      if (data.message === "password_breached") {
        return "Ce mot de passe figure dans une fuite connue. Choisissez-en un autre.";
      }
      if (data.message === "validation_failed") return "Données invalides.";
      if (data.message === "terms_not_accepted") {
        return "Vous devez accepter les CGU pour continuer.";
      }
      return data.message;
    }
  }
  if (err instanceof Error) return err.message;
  return "Une erreur est survenue.";
};
